import moment from 'moment';
import {
  Form,
  Divider,
  Typography,
  Tag,
  Spin,
  Row,
  Col,
  Card,
  Empty,
  Space,
  Input,
  Pagination,
} from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import store from '../../../redux/store';
import { mapScreeningToNodeParams } from '../../../utils/alkymation/screeningMapper';
import { getAllCustomSearchs } from '../../../API/services/screeningService';
import { t } from '../../../i18n/i18n';
import { setWorkflowTriggerScreening } from '../../../redux/actions/screeningActions';
import { EVALUATION_TEMPLATES, TRIGGER_TEMPLATES } from '../../../constants/alkymation';
import BaseModalStrategy from './BaseModalStrategy';
import './ScreeningStrategy.scss';

const { Title, Text } = Typography;

class ScreeningStrategy extends BaseModalStrategy {
  constructor() {
    super();
    this.screenings = [];
    this.loadingScreenings = false;
    this.selectedScreeningId = null;
    this.currentPage = 1;
    this.searchTerm = '';
    this.setScreenings = null;
    this.setLoadingScreenings = null;
    this.setSelectedScreeningId = null;
    this.setCurrentPage = null;
    this.setSearchTerm = null;
  }

  initializeWithHooks({
    setScreenings,
    setLoadingScreenings,
    setSelectedScreeningId,
    setCurrentPage,
    setSearchTerm,
  }) {
    this.setScreenings = setScreenings;
    this.setLoadingScreenings = setLoadingScreenings;
    this.setSelectedScreeningId = setSelectedScreeningId;
    this.setCurrentPage = setCurrentPage;
    this.setSearchTerm = setSearchTerm;
  }

  async loadScreenings(searchTerm = '') {
    this.setLoadingScreenings?.(true);
    try {
      const state = store?.getState();
      const workspace = state?.auth?.currentWorkspace;

      const response = await getAllCustomSearchs({
        status: 'open',
        workspace,
        limit: 100,
        offset: 0,
        inuse: false,
        psychotechnics: false,
        sort: 'createdAt',
        search: searchTerm,
      });

      let screeningsList = [];

      if (response?.collection && Array.isArray(response?.collection)) {
        screeningsList = response?.collection;
      } else if (Array.isArray(response)) {
        screeningsList = response;
      } else if (response?.data && Array.isArray(response?.data)) {
        screeningsList = response?.data;
      }

      const isTriggerNode =
        this.selectedNode?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE;
      const { workflowTriggerScreening } = state?.screenings || {};

      if (!isTriggerNode && workflowTriggerScreening) {
        screeningsList = screeningsList?.filter((screening) => {
          const screeningId = screening?.screeningId || screening?.id;
          return screeningId !== workflowTriggerScreening;
        });
      }

      this.setScreenings?.(screeningsList);
      return screeningsList;
    } catch (error) {
      console.error('Error loading screenings:', error);
      this.setScreenings?.([]);
      return [];
    } finally {
      this.setLoadingScreenings?.(false);
    }
  }

  handleScreeningSelect(screening, screeningId) {
    this.setSelectedScreeningId?.(screeningId);
    this.setCurrentPage?.(1);

    const state = store?.getState();
    const context = {
      workspace: state?.auth?.currentWorkspace,
      user: state?.auth?.user,
    };

    const isTriggerNode =
      this.selectedNode?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE;
    if (isTriggerNode) {
      store?.dispatch(setWorkflowTriggerScreening(screeningId));
    }

    const templateId = this.selectedNode?.template?.templateId;
    const mappedParams = mapScreeningToNodeParams(templateId, screening, context);

    const processedParams = {};

    Object.keys(mappedParams || {}).forEach((key) => {
      const value = mappedParams?.[key];

      const param = this.selectedNode?.template?.parameters?.find((p) => {
        if (p?.id === key) return true;

        if (!p?.name) return false;
        const normalizedParamName = p?.name?.toUpperCase()?.replace(/_/g, '');
        const normalizedKey = key?.toUpperCase()?.replace(/_/g, '');

        return normalizedParamName?.includes(normalizedKey);
      });

      const formFieldName = param ? param?.id || param?.name : key;

      const isDateField = (param && param?.type === 'date') || key?.toLowerCase()?.includes('date');

      if (isDateField && value) {
        try {
          let momentDate;
          if (typeof value === 'string') {
            momentDate = moment(value);
          } else if (moment.isMoment(value)) {
            momentDate = value;
          } else {
            momentDate = moment(value);
          }

          if (momentDate && momentDate?.isValid()) {
            processedParams[formFieldName] = momentDate;
          } else {
            processedParams[formFieldName] = value;
          }
        } catch (error) {
          console.error('Error converting date:', error);
          processedParams[formFieldName] = value;
        }
      } else {
        processedParams[formFieldName] = value;
      }
    });

    processedParams.screeningId = screeningId;

    if (this.form) {
      this.form?.setFieldsValue(processedParams);
    } else {
      console.error('Form is not initialized in ScreeningStrategy');
    }
  }

  renderScreeningCard(screening, selectedScreeningId) {
    const screeningId = screening?.screeningId || screening?.id;
    const isSelected = selectedScreeningId === screeningId;
    const name = screening?.recruitSessionStageName || screening?.name || 'Sin nombre';
    const isDraft = screening?.isDraft || false;
    const isActive = screening?.isActive === 1;

    const invitations = parseInt(screening?.invitationsSent || '0', 10) || 0;
    const started = parseInt(screening?.evaluationsStarted || '0', 10) || 0;
    const finished = parseInt(screening?.evaluationsFinished || '0', 10) || 0;

    return (
      <Col xs={24} sm={12} lg={12} xl={12} key={screeningId}>
        <Card
          hoverable
          className={`screening-card ${isSelected ? 'selected' : ''}`}
          onClick={() => this.handleScreeningSelect(screening, screeningId)}
          style={{
            borderColor: isSelected ? '#52c41a' : '#d9d9d9',
            borderWidth: isSelected ? '2px' : '1px',
            backgroundColor: isSelected ? '#f6ffed' : '#fff',
          }}
        >
          <div className="screening-card-content">
            {isSelected && (
              <CheckCircleOutlined
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  fontSize: '20px',
                  color: '#52c41a',
                }}
              />
            )}

            <Typography.Title level={5} ellipsis={{ rows: 1 }} style={{ marginBottom: '8px' }}>
              {name}
            </Typography.Title>

            <div className="screening-meta">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('ALKYMATION_CREATED_BY')}:{' '}
                  {screening?.partnerFirstName || screening?.partnerLastName
                    ? `${screening?.partnerFirstName || ''} ${
                        screening?.partnerLastName || ''
                      }`.trim()
                    : 'N/A'}
                </Text>

                <div style={{ marginTop: '8px' }}>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '11px',
                      display: 'block',
                      marginBottom: '4px',
                    }}
                  >
                    {t('ALKYMATION_PROGRESS')}
                  </Text>
                  <Space size="small" style={{ fontSize: '11px' }}>
                    <span>
                      {t('ALKYMATION_INVITATIONS')}: {invitations}
                    </span>
                    <Divider type="vertical" />
                    <span>
                      {t('ALKYMATION_STARTED')}: {started}
                    </span>
                    <Divider type="vertical" />
                    <span>
                      {t('ALKYMATION_COMPLETED')}: {finished}
                    </span>
                  </Space>
                </div>

                <Text type="secondary" style={{ fontSize: '11px' }}>
                  <CalendarOutlined /> {t('ALKYMATION_START_DATE')}:{' '}
                  {screening?.recruitSessionStageStartDate
                    ? new Date(screening?.recruitSessionStageStartDate).toLocaleDateString()
                    : 'N/A'}
                </Text>

                <div className="screening-tags" style={{ marginTop: '8px' }}>
                  {isDraft && <Tag color="orange">{t('ALKYMATION_DRAFT')}</Tag>}
                  <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? t('ALKYMATION_ACTIVE') : t('ALKYMATION_INACTIVE')}
                  </Tag>
                  {screening?.proctoringEnabled === 1 && (
                    <Tag color="blue">{t('ALKYMATION_PROCTORING')}</Tag>
                  )}
                  {screening?.language && <Tag>{screening?.language?.toUpperCase()}</Tag>}
                </div>
              </Space>
            </div>
          </div>
        </Card>
      </Col>
    );
  }

  renderParameterFormItems(parameters = []) {
    const isAssessmentInvitation =
      this.selectedNode?.template?.templateId === 'assessmentInvitation';

    return parameters?.map((parameter) => {
      const shouldHide = isAssessmentInvitation && parameter?.type !== 'date';

      return (
        <Form.Item
          key={parameter?.id}
          name={parameter?.id}
          label={
            <div className="parameter-label">
              <span>{t(parameter?.name)}</span>
              {parameter?.required && (
                <Tag color="red" size="small">
                  {t('ALKYMATION_REQUIRED')}
                </Tag>
              )}
            </div>
          }
          rules={[
            {
              required: parameter?.required,
              message: `${t(parameter?.name)} ${t('ALKYMATION_FIELD_REQUIRED')}`,
            },
          ]}
          help={t(parameter?.description)}
          style={{ display: shouldHide ? 'none' : undefined }}
        >
          {this.renderParameterField(parameter)}
        </Form.Item>
      );
    });
  }

  handleSearchChange(value) {
    this.setSearchTerm?.(value);

    if (value === '' || value === null || value === undefined) {
      this.setCurrentPage?.(1);
      this.loadScreenings('');
    }
  }

  handleSearchPressEnter(e) {
    const value = e.target.value;
    this.setSearchTerm?.(value);
    this.setCurrentPage?.(1);
    this.loadScreenings(value);
  }

  handlePageChange(page) {
    this.setCurrentPage?.(page);
  }

  renderBody({
    screenings,
    loadingScreenings,
    selectedScreeningId,
    templateId,
    currentPage,
    searchTerm,
  }) {
    const ITEMS_PER_PAGE = 4;

    let sortedScreenings = [...(screenings || [])];
    if (selectedScreeningId) {
      const selectedIndex = sortedScreenings.findIndex((screening) => {
        const screeningId = screening?.screeningId || screening?.id;
        return screeningId === selectedScreeningId;
      });

      if (selectedIndex > -1) {
        const selectedScreening = sortedScreenings.splice(selectedIndex, 1)[0];
        sortedScreenings.unshift(selectedScreening);
      }
    }

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedScreenings = sortedScreenings?.slice(startIndex, endIndex) || [];

    return (
      <>
        <Title level={5}>
          <SettingOutlined />{' '}
          {templateId === EVALUATION_TEMPLATES.ASSESSMENT_INVITATION
            ? t('ALKYMATION_TRIGGER_CONFIGURATION_ASSESSMENT')
            : t('ALKYMATION_TRIGGER_CONFIGURATION')}
        </Title>

        <div className="screening-selector">
          <div className="parameter-label">
            <span>
              {templateId === EVALUATION_TEMPLATES.ASSESSMENT_INVITATION
                ? t('ALKYMATION_SELECT_SCREENING_ASSESSMENT')
                : t('ALKYMATION_SELECT_SCREENING')}
            </span>
            <Tag color="red" size="small">
              {t('ALKYMATION_REQUIRED')}
            </Tag>
          </div>

          <Form.Item name="screeningId" style={{ marginBottom: 0 }}>
            <Input type="hidden" />
          </Form.Item>

          <div className="screening-search">
            <Input
              placeholder={t('ALKYMATION_SEARCH_SCREENINGS')}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => this.handleSearchChange(e.target.value)}
              onPressEnter={(e) => this.handleSearchPressEnter(e)}
              allowClear
            />
          </div>

          <div className="screening-cards-container">
            {loadingScreenings ? (
              <div className="text-center py-4">
                <Spin size="large" tip={t('ALKYMATION_LOADING_SCREENINGS')} />
              </div>
            ) : screenings?.length === 0 ? (
              <Empty
                description={t('ALKYMATION_NO_ACTIVE_SCREENINGS')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  {paginatedScreenings?.map((screening) =>
                    this.renderScreeningCard(screening, selectedScreeningId)
                  )}
                </Row>
                {screenings?.length > ITEMS_PER_PAGE && (
                  <div className="screening-pagination">
                    <Pagination
                      current={currentPage}
                      total={screenings?.length}
                      pageSize={ITEMS_PER_PAGE}
                      onChange={(page) => this.handlePageChange(page)}
                      showSizeChanger={false}
                      showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} screenings`}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Divider />
        <Title level={5}>
          <SettingOutlined /> {t('ALKYMATION_PARAMETERS')}
        </Title>
        {this.renderParameterFormItems(this.selectedNode?.template?.parameters)}
      </>
    );
  }
}

export default ScreeningStrategy;
